import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import axios from "../../api/axios";
import { getAttributeValue } from "../../utils/utils";
import APRComment from "./APRComment.js/AprComments";
import APRmemo from "./APRComment.js/APRmemo";

const PROJECT_PROGRAM = "g3wMUKEMmH3";
const PROJECT_TYPE_ATTRIBUTE = "wtf3BR2YO8w";
const PROJECT_TYPE_VALUE = "Programme";
const IMPLEMENTATION_STAGE = "rRWa5ghYO35";
const EXPENDITURE_ELEMENT = "jr8gk707kAw";
const IMPLEMENTATION_STATUS_ELEMENT = "f1T48vHfJc1";

function getStageEventValue(reports, trackedEntity, stageId, dataElementId) {
  const stageEvent = reports.find(
    (report) =>
      report.trackedEntity === trackedEntity
      && report.programStage === stageId
      && Array.isArray(report.dataValues)
      && report.dataValues.length > 0,
  );

  if (!stageEvent) {
    return "";
  }

  const match = stageEvent.dataValues.find(
    (dataValue) => dataValue.dataElement === dataElementId,
  );

  return match?.value ?? "";
}

const Table_8 = forwardRef(({ year, district, period, hideTableDis }, ref) => {
  const [tableData, setTableData] = useState([]);

  const selectedYear = useMemo(() => {
    const parsed = parseInt(year, 10);
    return Number.isFinite(parsed) ? parsed : new Date().getFullYear();
  }, [year]);

  useEffect(() => {
    getProjects();
  }, [selectedYear, district, period]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "Table_8",
      tableData,
    }),
  }));

  async function getProjects() {
    if (!district) {
      setTableData([]);
      return;
    }

    try {
      const startDate = `${selectedYear}-01-01`;
      const endDate = `${selectedYear}-12-31`;

      const [entitiesResp, eventsResp] = await Promise.all([
        axios.get(
          `/tracker/trackedEntities?orgUnit=${district}&program=${PROJECT_PROGRAM}&filter=${PROJECT_TYPE_ATTRIBUTE}:EQ:${PROJECT_TYPE_VALUE}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
        axios.get(
          `/tracker/events?program=${PROJECT_PROGRAM}&orgUnit=${district}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
      ]);

      const programmes = entitiesResp.data.instances || [];
      const reports = eventsResp.data.instances || [];

      if (programmes.length === 0) {
        setTableData([]);
        return;
      }

      const temps = programmes.map((programme, idx) => {
        const contractSum = getAttributeValue("Contract Sum", programme);
        const expenditureRaw = getStageEventValue(
          reports,
          programme.trackedEntity,
          IMPLEMENTATION_STAGE,
          EXPENDITURE_ELEMENT,
        );
        const implementationStatusRaw = getStageEventValue(
          reports,
          programme.trackedEntity,
          IMPLEMENTATION_STAGE,
          IMPLEMENTATION_STATUS_ELEMENT,
        );

        const expenditure = parseFloat(expenditureRaw) || 0;
        const contractSumValue = parseFloat(contractSum) || 0;

        return {
          no: idx + 1,
          description: getAttributeValue("Description", programme),
          dimension: getAttributeValue("Development Dimension", programme),
          contractSum,
          fundingSource: getAttributeValue("Primary Funding Source", programme),
          dateStarted: getAttributeValue("Start Date", programme),
          expectedCompletion: getAttributeValue("Expected Completion Date", programme),
          expenditure: expenditureRaw || 0,
          outstanding: contractSumValue - expenditure,
          implementationStatus: implementationStatusRaw || "",
          beneficiariesMale: getAttributeValue("Total Male Beneficiary", programme),
          beneficiariesFemale: getAttributeValue("Total Female Beneficiary", programme),
          remarks: getAttributeValue("Remarks", programme),
        };
      });

      setTableData(temps);
    } catch (err) {
      console.error("Error in getProjects:", err);
      setTableData([]);
    }
  }

  const tableStyle = {
    tableLayout: "fixed",
    border: "1px solid #000",
    borderCollapse: "collapse",
    width: "100%",
    marginTop: "20px",
    minWidth: "950px",
  };

  const thStyle = {
    border: "1px solid #000",
    fontWeight: "bold",
    backgroundColor: "#d4edda",
    textAlign: "center",
    verticalAlign: "middle",
  };

  const tdStyle = {
    border: "1px solid #000",
    verticalAlign: "top",
    padding: "8px",
  };

  return (
    <div className="col-12">
      <h3>Table 8 – Programmes (Non-Physical Projects) Register</h3>
      <div className="card">
        <div className="card-header"></div>
        <div className="card-body">
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table8-${year}`}
            hideTableDis={hideTableDis}
          />

          <div className="screen-view-tables">
            <h4>Part 1: Programme Details, Dates, and Financials</h4>
            <div className="table-responsive">
              <table className="table table-bordered" style={tableStyle}>
                <thead>
                  <tr style={{ backgroundColor: "#d4edda", fontWeight: "bold" }}>
                    <th style={{ ...thStyle, width: "5%" }} rowSpan="2">#</th>
                    <th style={{ ...thStyle, width: "25%" }} rowSpan="2">Programme Description</th>
                    <th style={{ ...thStyle, width: "20%" }} rowSpan="2">Development Dimension of Policy Framework</th>
                    <th style={{ ...thStyle, width: "15%" }} rowSpan="2">Amount Involved GH¢</th>
                    <th style={{ ...thStyle, width: "15%" }} rowSpan="2">Source of Funding</th>
                    <th style={{ ...thStyle, width: "10%" }} rowSpan="2">Date Started</th>
                    <th style={{ ...thStyle, width: "10%" }} rowSpan="2">Expected Date of Completion</th>
                    <th style={{ ...thStyle, width: "15%" }} colSpan="2">Balance</th>
                  </tr>
                  <tr style={{ backgroundColor: "#d4edda", fontWeight: "bold" }}>
                    <th style={{ ...thStyle, width: "7.5%" }}>Expenditure to Date</th>
                    <th style={{ ...thStyle, width: "7.5%" }}>Outstanding Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.length > 0 ? (
                    tableData.map((row, index) => (
                      <tr key={index}>
                        <td style={tdStyle}>{row.no}</td>
                        <td style={tdStyle}>{row.description}</td>
                        <td style={tdStyle}>{row.dimension}</td>
                        <td style={tdStyle}>{row.contractSum}</td>
                        <td style={tdStyle}>{row.fundingSource}</td>
                        <td style={tdStyle}>{row.dateStarted}</td>
                        <td style={tdStyle}>{row.expectedCompletion}</td>
                        <td style={tdStyle}>{row.expenditure}</td>
                        <td style={tdStyle}>{row.outstanding}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: "center" }}>
                        No data available for {selectedYear}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <h4 style={{ marginTop: "30px" }}>Part 2: Status, Pictures, and Beneficiaries/Remarks</h4>
            <div className="table-responsive">
              <table className="table table-bordered" style={{ ...tableStyle, minWidth: "550px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#d4edda", fontWeight: "bold" }}>
                    <th style={{ ...thStyle, width: "10%" }} rowSpan="2">Implementation Status (%)</th>
                    <th style={{ ...thStyle, width: "10%" }} rowSpan="2">Pictures</th>
                    <th style={{ ...thStyle, width: "20%" }} colSpan="2">Total Beneficiaries</th>
                    <th style={{ ...thStyle, width: "20%" }} rowSpan="2">Remarks</th>
                  </tr>
                  <tr style={{ backgroundColor: "#d4edda", fontWeight: "bold" }}>
                    <th style={{ ...thStyle, width: "10%" }}>Male</th>
                    <th style={{ ...thStyle, width: "10%" }}>Female</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.length > 0 ? (
                    tableData.map((row, index) => (
                      <tr key={index}>
                        <td style={tdStyle}>{row.implementationStatus}</td>
                        <td style={tdStyle} />
                        <td style={tdStyle}>{row.beneficiariesMale}</td>
                        <td style={tdStyle}>{row.beneficiariesFemale}</td>
                        <td style={tdStyle}>{row.remarks}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: "center" }}>
                        No data available for {selectedYear}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="table-printable-version" style={{ display: "none" }}>
            <div className="table-responsive">
              <table className="table table-bordered" style={{ ...tableStyle, tableLayout: "auto" }}>
                <thead>
                  <tr style={{ backgroundColor: "#d4edda", fontWeight: "bold" }}>
                    <th style={{ ...thStyle, fontSize: "10px" }} rowSpan="2">No.</th>
                    <th style={{ ...thStyle, fontSize: "10px" }} rowSpan="2">Programme / Dimension</th>
                    <th style={{ ...thStyle, fontSize: "10px" }} rowSpan="2">Amount GH¢ / Source</th>
                    <th style={{ ...thStyle, fontSize: "10px" }} rowSpan="2">Start/Expected Completion Dates</th>
                    <th style={{ ...thStyle, fontSize: "10px" }} rowSpan="2">Expenditure / Outstanding Balance</th>
                    <th style={{ ...thStyle, fontSize: "10px" }} rowSpan="2">Impl. Status (%)</th>
                    <th style={{ ...thStyle, fontSize: "10px" }} colSpan="2">Total Beneficiaries</th>
                    <th style={{ ...thStyle, fontSize: "10px" }} rowSpan="2">Remarks</th>
                  </tr>
                  <tr style={{ backgroundColor: "#d4edda", fontWeight: "bold" }}>
                    <th style={{ ...thStyle, fontSize: "10px" }}>Male</th>
                    <th style={{ ...thStyle, fontSize: "10px" }}>Female</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index}>
                      <td style={{ ...tdStyle, fontSize: "9px" }}>{row.no}</td>
                      <td style={{ ...tdStyle, fontSize: "9px" }}>
                        **Desc:** {row.description}<br />
                        **Dim:** {row.dimension}
                      </td>
                      <td style={{ ...tdStyle, fontSize: "9px" }}>
                        **Amt:** {row.contractSum}<br />
                        **Src:** {row.fundingSource}
                      </td>
                      <td style={{ ...tdStyle, fontSize: "9px" }}>
                        **Start:** {row.dateStarted}<br />
                        **Exp. Comp:** {row.expectedCompletion}
                      </td>
                      <td style={{ ...tdStyle, fontSize: "9px" }}>
                        **Exp.:** {row.expenditure}<br />
                        **Out.:** {row.outstanding}
                      </td>
                      <td style={{ ...tdStyle, fontSize: "9px" }}>{row.implementationStatus}</td>
                      <td style={{ ...tdStyle, fontSize: "9px" }}>{row.beneficiariesMale}</td>
                      <td style={{ ...tdStyle, fontSize: "9px" }}>{row.beneficiariesFemale}</td>
                      <td style={{ ...tdStyle, fontSize: "9px" }}>{row.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table8-${year}`}
            hideTableDis={hideTableDis}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Table_8;
